import { CircleMarker, Polyline, Tooltip } from "react-leaflet";
import { bearingDeg, destinationPoint, haversineM } from "../map/geo";
import type { FlightRoute } from "../map/types";

const SEGMENTS = 64;
// Airport codes only when zoomed in enough that the labels mean something; at continental zoom
// they pile onto exactly the spots where traffic already clusters.
const LABEL_MIN_ZOOM = 6;

/** The great circle from origin to destination, as map coordinates.
 *  A two-point polyline would be a straight line on the Mercator map — a rhumb line, not the route. */
function greatCircle(a: { lat: number; lon: number }, b: { lat: number; lon: number }): [number, number][] {
  const total = haversineM(a.lat, a.lon, b.lat, b.lon);
  const bearing = bearingDeg(a.lat, a.lon, b.lat, b.lon);
  const points: [number, number][] = [];
  let previousLon = a.lon;
  for (let i = 0; i <= SEGMENTS; i++) {
    const [lat, lon] = destinationPoint(a.lat, a.lon, bearing, (total * i) / SEGMENTS);
    // Unwrap: destinationPoint normalises to (-180, 180], so a route over the antimeridian would
    // jump from +179 to -179 and draw a line across the entire map.
    let unwrapped = lon;
    while (unwrapped - previousLon > 180) unwrapped -= 360;
    while (unwrapped - previousLon < -180) unwrapped += 360;
    previousLon = unwrapped;
    points.push([lat, unwrapped]);
  }
  return points;
}

/**
 * Where the selected flight has come from and is going, drawn through the aircraft itself.
 *
 * First version drew one arc from origin to destination. Verifying it on production showed why that
 * is wrong: real flights don't fly the great circle — a Pegasus A321 from Istanbul to Graz was 149 km
 * off it, routed via Bratislava — so at any useful zoom the arc was entirely off-screen, and at low
 * zoom it misrepresented the actual path. Two legs through the aircraft's current position, the way
 * flight trackers draw it, are always visible and never claim the aircraft is somewhere it isn't:
 * origin → aircraft fainter (flown, approximately), aircraft → destination brighter (ahead).
 *
 * Verified routes only. An arc is a stronger claim than a line of text, and a stale mapping drawn as
 * a confident curve would be worse than none. Never moves the map.
 */
export default function RouteArcLayer({
  route,
  aircraft,
  zoom,
}: {
  route: FlightRoute | null;
  aircraft: { lat: number; lon: number } | undefined;
  zoom: number;
}) {
  if (!route || !route.verified || !aircraft) return null;
  const { origin, destination } = route;
  const flown = greatCircle(origin, aircraft);
  const ahead = greatCircle(aircraft, destination);
  const airports = [
    { key: "origin", code: origin.iata ?? origin.icao, lat: origin.lat, lon: origin.lon },
    { key: "destination", code: destination.iata ?? destination.icao, lat: destination.lat, lon: destination.lon },
  ];

  return (
    <>
      <Polyline positions={flown} interactive={false} pathOptions={{ color: "#8ea0c0", weight: 1.5, opacity: 0.3, dashArray: "2 6" }} />
      <Polyline positions={ahead} interactive={false} pathOptions={{ color: "#c7d3e8", weight: 1.5, opacity: 0.55, dashArray: "2 6" }} />
      {airports.map((ap) => (
        <CircleMarker
          key={ap.key}
          center={[ap.lat, ap.lon]}
          radius={3}
          interactive={false}
          pathOptions={{ color: "#8ea0c0", fillColor: "#0b1220", fillOpacity: 1, weight: 1.5 }}
        >
          {zoom >= LABEL_MIN_ZOOM && ap.code && (
            <Tooltip permanent direction="top" offset={[0, -4]} className="tick-label airport-label">
              {ap.code}
            </Tooltip>
          )}
        </CircleMarker>
      ))}
    </>
  );
}
